import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, PageHeader, Section } from '../../../components/layout/primitives';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import {
  featureService,
  type Feature,
  type FeatureCategory,
  type FeatureStatus,
  type FeatureType,
} from '../../../services/featureService';
import FeatureModal from './components/FeatureModal';
import { Badge } from '../../../components/ui/Badge';
import { Spinner } from '../../../components/ui/Button';
import { SelectField } from '../../../components/ui/Field';
import { Table } from '../../../components/ui/Table';

const TYPE_LABELS: Record<FeatureType, string> = {
  boolean: 'Boolean',
  limit: 'Limit',
  option: 'Option',
};

const CATEGORY_LABELS: Record<FeatureCategory, string> = {
  access: 'Access',
  capacity: 'Capacity',
  workflow: 'Workflow',
  reporting: 'Reporting',
  support: 'Support',
  integration: 'Integration',
};

const TYPE_COLORS: Record<FeatureType, string> = {
  boolean: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200',
  limit: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  option: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
};

const CATEGORY_COLORS: Record<FeatureCategory, string> = {
  access: 'bg-emerald-50 text-emerald-700',
  capacity: 'bg-sky-50 text-sky-700',
  workflow: 'bg-violet-50 text-violet-700',
  reporting: 'bg-orange-50 text-orange-700',
  support: 'bg-pink-50 text-pink-700',
  integration: 'bg-teal-50 text-teal-700',
};

const selectCls = 'h-9 rounded-xl border border-agentic-gray-300 bg-white px-3 text-sm text-agentic-text-primary transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kv-agentic-blue focus-visible:ring-offset-2';

type UsedFilter = 'all' | 'used' | 'unused';

type BlockedPod = { podId: string; podName: string };

const FeatureRegistryPage: React.FC = () => {
  const navigate = useNavigate();
  const [features, setFeatures] = useState<Feature[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<FeatureType | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<FeatureCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<FeatureStatus>('active');
  const [usedFilter, setUsedFilter] = useState<UsedFilter>('all');
  const [page, setPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Feature | undefined>(undefined);

  const [confirmDelete, setConfirmDelete] = useState<Feature | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<Feature | null>(null);
  const [archiveBlocked, setArchiveBlocked] = useState<{ feature: Feature; pods: BlockedPod[] } | null>(null);
  const [actioning, setActioning] = useState(false);

  const PAGE_SIZE = 20;
  const hasActiveFilters = !!search || typeFilter !== 'all' || categoryFilter !== 'all' || usedFilter !== 'all';

  const loadFeatures = useCallback(async () => {
    setLoading(true);
    try {
      const res = await featureService.list({
        search: search || undefined,
        type: typeFilter === 'all' ? undefined : typeFilter,
        category: categoryFilter === 'all' ? undefined : categoryFilter,
        status: statusFilter,
        used: usedFilter === 'all' ? undefined : usedFilter === 'used',
        page,
        page_size: PAGE_SIZE,
      });
      setFeatures(res.features);
      setTotal(res.total);
    } catch {
      // apiClient already surfaces a toast for the failed request.
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, categoryFilter, statusFilter, usedFilter, page]);

  useEffect(() => {
    const t = setTimeout(loadFeatures, 300);
    return () => clearTimeout(t);
  }, [loadFeatures]);

  const handleSaved = () => {
    setModalOpen(false);
    setEditing(undefined);
    loadFeatures();
  };

  const doArchive = async () => {
    if (!confirmArchive) return;
    setActioning(true);
    try {
      await featureService.archive(confirmArchive.id);
      setConfirmArchive(null);
      loadFeatures();
    } catch (e: any) {
      const err = e?.details?.error;
      if (err?.code === 'IN_USE_BY_LIVE_POD') {
        const pods: BlockedPod[] = (err.details?.pods || []).map((p: any) => ({ podId: p.podId, podName: p.podName }));
        setArchiveBlocked({ feature: confirmArchive, pods });
      }
      // Other failures: apiClient already toasted the message.
      setConfirmArchive(null);
    } finally {
      setActioning(false);
    }
  };

  const doRestore = async (feature: Feature) => {
    try {
      await featureService.restore(feature.id);
      loadFeatures();
    } catch {
      // apiClient already surfaces a toast for the failed request.
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setActioning(true);
    try {
      await featureService.hardDelete(confirmDelete.id);
      setConfirmDelete(null);
      loadFeatures();
    } catch {
      // apiClient already surfaces a toast for the failed request.
    } finally {
      setActioning(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Page>
      <PageHeader
        title="Feature Registry"
        subtitle="Define the plan capabilities available across all AI Pods"
        aside={
          <Button size="sm" onClick={() => { setEditing(undefined); setModalOpen(true); }}>
            + New Feature
          </Button>
        }
      />

      <Section>
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <Input
            placeholder="Search by key or name…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            size="sm"
            className="w-60"
          />
          <SelectField className={selectCls} value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value as any); setPage(1); }}>
            <option value="all">All types</option>
            <option value="boolean">Boolean</option>
            <option value="limit">Limit</option>
            <option value="option">Option</option>
          </SelectField>
          <SelectField className={selectCls} value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value as any); setPage(1); }}>
            <option value="all">All categories</option>
            {(Object.keys(CATEGORY_LABELS) as FeatureCategory[]).map((c) => (
              <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
            ))}
          </SelectField>
          <SelectField className={selectCls} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as FeatureStatus); setPage(1); }}>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </SelectField>
          <SelectField className={selectCls} value={usedFilter} onChange={(e) => { setUsedFilter(e.target.value as UsedFilter); setPage(1); }}>
            <option value="all">Used or not</option>
            <option value="used">Used</option>
            <option value="unused">Not used</option>
          </SelectField>
          {total > 0 && (
            <span className="ml-auto text-xs text-agentic-text-secondary">{total} feature{total !== 1 ? 's' : ''}</span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-12 justify-center text-agentic-text-secondary text-sm">
            <Spinner className="h-4 w-4 animate-spin rounded-full border-2 border-agentic-text-secondary border-t-transparent" />
            Loading…
          </div>
        ) : features.length === 0 ? (
          <div className="py-16 text-center">
            {hasActiveFilters ? (
              <>
                <p className="text-agentic-text-secondary text-sm">No features found.</p>
                <Button tone="brand-text"
                  className="mt-2 text-xs hover:underline"
                  onClick={() => { setSearch(''); setTypeFilter('all'); setCategoryFilter('all'); setUsedFilter('all'); setPage(1); }}
                >
                  Clear filters
                </Button>
              </>
            ) : (
              <>
                <p className="text-agentic-text-secondary text-sm">
                  No Features yet. Features are plan capabilities Pods can offer, like team seats or approvals.
                </p>
                <Button size="sm" className="mt-4" onClick={() => { setEditing(undefined); setModalOpen(true); }}>
                  + New Feature
                </Button>
              </>
            )}
          </div>
        ) : (
          <Card className="overflow-hidden p-0">
            <div className="overflow-x-auto">
              <Table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-agentic-gray-100 bg-agentic-gray-50 text-left">
                    <th className="py-3 px-4 text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide">Key</th>
                    <th className="py-3 px-4 text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide">Name</th>
                    <th className="py-3 px-4 text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide">Type</th>
                    <th className="py-3 px-4 text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide">Category</th>
                    <th className="py-3 px-4 text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide">Default</th>
                    <th className="py-3 px-4 text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide text-center">Pods</th>
                    <th className="py-3 px-4 text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide">Last updated</th>
                    <th className="py-3 px-4 text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-agentic-gray-100">
                  {features.map((f) => (
                    <tr
                      key={f.id}
                      className="cursor-pointer hover:bg-agentic-gray-50 transition-colors"
                      onClick={() => navigate(`/super-features/${f.id}`)}
                    >
                      <td className="py-3 px-4 font-mono text-xs text-agentic-text-secondary">{f.key}</td>
                      <td className="py-3 px-4 font-medium text-agentic-text-primary">{f.name}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${TYPE_COLORS[f.type]}`}>
                          {TYPE_LABELS[f.type]}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${CATEGORY_COLORS[f.category]}`}>
                          {CATEGORY_LABELS[f.category]}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-agentic-text-secondary">
                        {String(f.defaultValue)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {f.podCount > 0 ? (
                          <Badge className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-full text-xs font-semibold bg-kv-agentic-blue/10 text-kv-agentic-blue">
                            {f.podCount}
                          </Badge>
                        ) : (
                          <span className="text-xs text-agentic-gray-300">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-xs text-agentic-text-secondary whitespace-nowrap">
                        {f.updatedAt
                          ? new Date(f.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </td>
                      <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button tone="custom"
                            onClick={() => { setEditing(f); setModalOpen(true); }}
                            className="px-2.5 py-1 rounded-lg text-xs font-medium text-kv-agentic-blue hover:bg-blue-50 transition"
                          >
                            Edit
                          </Button>
                          {f.status === 'active' ? (
                            <Button tone="custom"
                              onClick={() => setConfirmArchive(f)}
                              className="px-2.5 py-1 rounded-lg text-xs font-medium text-amber-600 hover:bg-amber-50 transition"
                            >
                              Archive
                            </Button>
                          ) : (
                            <Button tone="custom"
                              onClick={() => doRestore(f)}
                              className="px-2.5 py-1 rounded-lg text-xs font-medium text-emerald-600 hover:bg-emerald-50 transition"
                            >
                              Restore
                            </Button>
                          )}
                          {f.podCount === 0 && (
                            <Button tone="custom"
                              onClick={() => setConfirmDelete(f)}
                              className="px-2.5 py-1 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 transition"
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-agentic-gray-100 px-4 py-3">
                <p className="text-xs text-agentic-text-secondary">{total} total</p>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    ← Prev
                  </Button>
                  <span className="text-xs text-agentic-text-secondary px-1">
                    {page} / {totalPages}
                  </span>
                  <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                    Next →
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </Section>

      {modalOpen && (
        <FeatureModal
          feature={editing}
          onClose={() => { setModalOpen(false); setEditing(undefined); }}
          onSaved={handleSaved}
        />
      )}

      <ConfirmationDialog
        isOpen={!!confirmArchive}
        title="Archive Feature"
        message={`Archive ${confirmArchive?.name}? It will be hidden for new Pods. Existing Pods and subscriptions won't change.`}
        confirmText="Archive"
        onConfirm={doArchive}
        onClose={() => setConfirmArchive(null)}
        loading={actioning}
      />

      <ConfirmationDialog
        isOpen={!!archiveBlocked}
        title="Can't Archive"
        variant="warning"
        message={
          <>
            <p className="mb-2">
              "{archiveBlocked?.feature.name}" is still used by {archiveBlocked?.pods.length} live Pod
              {archiveBlocked && archiveBlocked.pods.length !== 1 ? 's' : ''}. Remove it from those Pods first, then archive.
            </p>
            <ul className="space-y-1">
              {archiveBlocked?.pods.map((p) => (
                <li key={p.podId}>
                  <a
                    href={`/super-ai-pod/${p.podId}/configure`}
                    className="text-kv-agentic-blue hover:underline text-sm"
                    onClick={(e) => { e.preventDefault(); setArchiveBlocked(null); navigate(`/super-ai-pod/${p.podId}/configure`); }}
                  >
                    {p.podName}
                  </a>
                </li>
              ))}
            </ul>
          </>
        }
        confirmText="Got it"
        cancelText="Close"
        onConfirm={() => setArchiveBlocked(null)}
        onClose={() => setArchiveBlocked(null)}
      />

      <ConfirmationDialog
        isOpen={!!confirmDelete}
        title="Delete Feature"
        message={`Permanently delete "${confirmDelete?.name}"? This cannot be undone.`}
        confirmText="Delete"
        onConfirm={doDelete}
        onClose={() => setConfirmDelete(null)}
        loading={actioning}
      />
    </Page>
  );
};

export default FeatureRegistryPage;
